import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ROLE_TR } from "@/utils/format";
import api from "@/lib/api";
import ConfettiTrigger from "@/components/ConfettiTrigger";
import { MarketRatesStrip } from "@/components/MarketRatesStrip";
import {
  LayoutDashboard, FileText, ShoppingCart, Users, Package, Warehouse,
  Factory, LogOut, UserCog, ClipboardList, Receipt, Settings as SettingsIcon,
  PanelLeftClose, PanelLeftOpen, Wallet, CalendarClock, ShieldCheck,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Panel", icon: LayoutDashboard, roles: ["admin", "sales", "hse"] },
  { to: "/teklifler", label: "Teklifler", icon: FileText, roles: ["admin", "sales"] },
  { to: "/siparisler", label: "Siparişler", icon: ShoppingCart, roles: ["admin", "sales"] },
  { to: "/depo", label: "Depo", icon: Warehouse, roles: ["admin", "warehouse"] },
  { to: "/uretim", label: "Üretim", icon: Factory, roles: ["admin", "production"] },
  { to: "/muhasebe", label: "Muhasebe", icon: Receipt, roles: ["admin", "accounting", "sales"] },
  { to: "/finans", label: "Finans", icon: Wallet, roles: ["admin"] },
  { to: "/personel", label: "Personel", icon: CalendarClock, roles: ["admin", "sub_admin", "accounting"] },
  { to: "/isg", label: "İSG", icon: ShieldCheck, roles: ["admin", "sub_admin", "hse"] },
  { to: "/musteriler", label: "Müşteriler", icon: Users, roles: ["admin", "sales"] },
  { to: "/urunler", label: "Ürünler", icon: Package, roles: ["admin", "sales", "warehouse", "production"] },
  { to: "/kullanicilar", label: "Kullanıcılar", icon: UserCog, roles: ["admin"] },
  { to: "/loglar", label: "Loglar", icon: ClipboardList, roles: ["admin"], badge: "suspicious" },
  { to: "/ayarlar", label: "Ayarlar", icon: SettingsIcon, roles: ["admin"] },
];

const COLLAPSED_KEY = "hakpi:sidebar:collapsed";

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  // Persist the collapsed preference across page reloads / navigations so
  // the sidebar stays where the user left it.
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSED_KEY) === "1");
  useEffect(() => {
    localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  const items = NAV.filter((n) => {
    if (user?.role === "admin") return true;
    if (user?.role === "sub_admin") return !["/ayarlar", "/kullanicilar", "/loglar"].includes(n.to);
    return n.roles.includes(user?.role);
  });

  // Admin için 60 sn'de bir şüpheli aktivite sayısını poll et → Loglar rozeti.
  const [suspiciousCount, setSuspiciousCount] = useState(0);
  useEffect(() => {
    if (user?.role !== "admin") return;
    let stopped = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/logs/suspicious", { params: { window_minutes: 60, threshold: 5 } });
        if (!stopped) setSuspiciousCount(data?.count || 0);
      } catch { /* sessizce yut */ }
    };
    tick();
    const iv = setInterval(tick, 60_000);
    return () => { stopped = true; clearInterval(iv); };
  }, [user?.role]);

  const doLogout = async () => {
    await logout();
    navigate("/giris", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5] text-[#09090B] flex">
      {/* Sidebar — width toggles between 64 (256px) and 16 (64px). Labels
          fade out when collapsed while icons stay centred so it still
          feels like a proper nav rail rather than a broken column. */}
      <aside
        className={`shrink-0 bg-white border-r-2 border-[#09090B] flex flex-col transition-[width] duration-200 ${
          collapsed ? "w-16" : "w-64"
        }`}
        data-testid="sidebar"
        data-collapsed={collapsed ? "true" : "false"}
      >
        <div className={`border-b-2 border-[#09090B] ${collapsed ? "px-2 py-4" : "px-5 py-6"} flex items-center gap-2`}>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-mono tracking-[0.2em] text-neutral-500">TEKLİF · SİPARİŞ</div>
              <div className="mt-1 text-2xl font-black tracking-tighter font-heading truncate">ATÖLYE OS</div>
            </div>
          )}
          <Button
            variant="outline"
            size="icon"
            className="rounded-none border-2 border-[#09090B] h-9 w-9 shrink-0 hover:bg-[#09090B] hover:text-white"
            onClick={() => setCollapsed((c) => !c)}
            title={collapsed ? "Menüyü genişlet" : "Menüyü daralt"}
            data-testid="sidebar-toggle"
          >
            {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
          </Button>
        </div>
        <nav className="flex-1 py-4">
          {items.map((it) => {
            const Icon = it.icon;
            const showBadge = it.badge === "suspicious" && suspiciousCount > 0;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                data-testid={`nav-${it.to.replace("/", "") || "dashboard"}`}
                title={collapsed ? (showBadge ? `${it.label} · ${suspiciousCount} şüpheli` : it.label) : undefined}
                className={({ isActive }) =>
                  `flex items-center gap-3 border-l-4 text-sm font-medium hover:bg-neutral-100 ${
                    collapsed ? "justify-center px-2 py-3" : "px-5 py-3"
                  } ${
                    isActive
                      ? "border-l-[#09090B] bg-neutral-100 font-bold"
                      : "border-l-transparent"
                  }`
                }
              >
                <div className="relative shrink-0">
                  <Icon size={18} strokeWidth={2.25} />
                  {showBadge && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 bg-red-600 text-white text-[9px] font-mono font-bold rounded-full flex items-center justify-center leading-none border border-white"
                      data-testid="nav-loglar-badge">
                      {suspiciousCount > 9 ? "9+" : suspiciousCount}
                    </span>
                  )}
                </div>
                {!collapsed && (
                  <span className="truncate flex-1 flex items-center justify-between gap-2">
                    <span className="truncate">{it.label}</span>
                    {showBadge && (
                      <span className="shrink-0 px-1.5 py-0.5 bg-red-600 text-white text-[9px] font-mono rounded">
                        {suspiciousCount}
                      </span>
                    )}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className={`border-t-2 border-[#09090B] ${collapsed ? "p-2" : "p-4"}`}>
          {!collapsed && (
            <>
              <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">Oturum</div>
              <div className="mt-1 text-sm font-semibold truncate" data-testid="current-user-name">{user?.name}</div>
              <div className="text-xs text-neutral-500 truncate" data-testid="current-user-email">{user?.email}</div>
              <div className="mt-2 inline-block px-2 py-0.5 border border-[#09090B] text-[10px] font-mono uppercase" data-testid="current-user-role">
                {ROLE_TR[user?.role] || user?.role}
              </div>
            </>
          )}
          <Button
            variant="outline"
            size={collapsed ? "icon" : "default"}
            className={`${collapsed ? "w-full h-10" : "mt-4 w-full"} rounded-none border-2 border-[#09090B] hover:bg-[#09090B] hover:text-white`}
            onClick={doLogout}
            title={collapsed ? "Çıkış Yap" : undefined}
            data-testid="logout-button"
          >
            <LogOut size={16} className={collapsed ? "" : "mr-2"} />
            {!collapsed && "Çıkış Yap"}
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="sticky top-0 z-30 bg-[#F4F4F5]/90 backdrop-blur-md border-b border-neutral-300" data-testid="global-market-bar">
          <div className="max-w-[1400px] mx-auto px-8 py-1.5 flex justify-end overflow-x-auto">
            <MarketRatesStrip />
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto p-8">
          <Outlet />
        </div>
      </main>
      <ConfettiTrigger />
    </div>
  );
}
